var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { NodeComponentType, TubeType, } from "./types.current";
import { systemDataConstants } from "api/system-data";
import { tubeDiameterOptionInitialIndex, tubeDiameterOptions, } from "store/types";
import { tubeDefaultLength } from "3d/constants/common";
import { getRotatedFootprint } from "3d/pallet";
export var getInitialTubeDiameterIndex = function (unitSystem, systemData) {
    var systemType = systemData.parameters[systemDataConstants.parameterNames.vacuumSystemType]
        .value === systemDataConstants.parameterValues.centralizedSystem
        ? "centralized"
        : "decentralized";
    return tubeDiameterOptionInitialIndex[unitSystem][systemType];
};
/*
 * Find where to drop a newly added cardboard box: the first free spot on a
 * grid (cells the size of the box footprint, anchored at `origin`), searched in
 * an outward spiral so the box lands as close to the existing boxes as
 * possible. The spiral starts at the origin, then right of it, then below that,
 * then below the origin, and so on clockwise — filling the gaps around the
 * existing arrangement before moving further out.
 *
 * A cell is "free" when a new (unrotated) box placed there would not overlap any
 * existing box. Existing boxes may have been dragged anywhere, rotated and
 * given a size of their own, so overlap is tested against each box's actual
 * footprint: `footprintOf` gives its unrotated world footprint (the new box's
 * by default), and rotation 90/270 swaps its X/Z extents.
 */
var MAX_SPIRAL_RING = 50;
export var findFreeCardboardBoxPosition = function (existingBoxes, newBoxFootprint, origin, footprintOf) {
    var e_1, _a;
    if (footprintOf === void 0) { footprintOf = function () { return newBoxFootprint; }; }
    var stepX = newBoxFootprint.x;
    var stepZ = newBoxFootprint.z;
    var newHalfX = newBoxFootprint.x / 2;
    var newHalfZ = newBoxFootprint.z / 2;
    // Allow edges to touch (a cell exactly one footprint away is free) without
    // floating-point noise flagging it as an overlap.
    var epsilon = 1e-4;
    var isFree = function (centerX, centerZ) {
        return existingBoxes.every(function (box) {
            var footprint = getRotatedFootprint(footprintOf(box), box.rotation);
            var boxHalfX = footprint.x / 2;
            var boxHalfZ = footprint.z / 2;
            var dx = Math.abs(centerX - box.position.x);
            var dz = Math.abs(centerZ - box.position.z);
            return (dx >= boxHalfX + newHalfX - epsilon ||
                dz >= boxHalfZ + newHalfZ - epsilon);
        });
    };
    var tryCell = function (gridX, gridZ) {
        var centerX = origin.x + gridX * stepX;
        var centerZ = origin.z + gridZ * stepZ;
        return isFree(centerX, centerZ) ? { x: centerX, z: centerZ } : null;
    };
    // The origin itself is the closest possible spot (used by the first box on a
    // layer, or when the original box has been dragged away).
    var atOrigin = tryCell(0, 0);
    if (atOrigin)
        return atOrigin;
    for (var ring = 1; ring <= MAX_SPIRAL_RING; ring++) {
        // Clockwise around the ring, starting at the right edge going down, so the
        // order is right-of-origin, below it, then around toward below-origin.
        var cells = [];
        for (var z = 0; z <= ring; z++)
            cells.push([ring, z]);
        for (var x = ring - 1; x >= -ring; x--)
            cells.push([x, ring]);
        for (var z = ring - 1; z >= -ring; z--)
            cells.push([-ring, z]);
        for (var x = -ring + 1; x <= ring; x++)
            cells.push([x, -ring]);
        try {
            for (var cells_1 = (e_1 = void 0, __values(cells)), cells_1_1 = cells_1.next(); !cells_1_1.done; cells_1_1 = cells_1.next()) {
                var _b = __read(cells_1_1.value, 2), gridX = _b[0], gridZ = _b[1];
                var free = tryCell(gridX, gridZ);
                if (free)
                    return free;
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (cells_1_1 && !cells_1_1.done && (_a = cells_1.return)) _a.call(cells_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
    }
    // Practically unreachable (50 rings ≈ 10k cells); fall back to a spot well
    // clear of the grid rather than overlapping.
    return { x: origin.x + (MAX_SPIRAL_RING + 1) * stepX, z: origin.z };
};
export var addEdgeToState = function (state, payload) {
    var defaultDiameterIndex = payload.initialTubeDiameterIndex;
    var blowOffDiameterIndex = payload.initialTubeDiameterIndex;
    state.edges[payload.edgeId] = {
        id: payload.edgeId,
        diameterDefaultTube: tubeDiameterOptions[payload.unitSystem][defaultDiameterIndex].value,
        diameterBlowOffTube: tubeDiameterOptions[payload.unitSystem][blowOffDiameterIndex].value,
        defaultDiameterIndex: defaultDiameterIndex,
        blowOffDiameterIndex: blowOffDiameterIndex,
        length: tubeDefaultLength,
        tubeType: TubeType.UNKNOWN,
        fromAttachmentId: payload.fromAttachmentId,
        toAttachmentId: payload.toAttachmentId,
    };
    state.attachments[payload.fromAttachmentId].edgeId = payload.edgeId;
    state.attachments[payload.toAttachmentId].edgeId = payload.edgeId;
};
export var updateTubeTypes = function (state) {
    var e_2, _a;
    var traversed = new Set();
    var notTraversed = new Set();
    var recursiveUpdateTubes = function (currentNode, currentType) {
        /* If the current node is a tube connection, update the type of that connection*/
        if (currentNode.type === NodeComponentType.TUBE_CONNECTION) {
            var tubeConnection = currentNode.component;
            tubeConnection.tubeType = currentType;
            currentNode.attachmentIds.forEach(function (attachmentId) {
                var attachment = state.attachments[attachmentId];
                attachment.tubeType = currentType;
            });
        }
        /* Look at all attachments for this node, if the attachment has an edge that
         * we have not yet traversed, update it's type and recursively call this
         * function for the node that the edge leads to */
        currentNode.attachmentIds.forEach(function (attachmentId) {
            var attachment = state.attachments[attachmentId];
            if (attachment.edgeId && !traversed.has(attachment.edgeId)) {
                /* The tube type changes when we encounter a vacuum pump. If the current
                 * attachment is the back of the pump (leading to the feed pressure point),
                 * the type of the edge and any nodes/edges that edge is connected to will be
                 * "compressed_air". If it's the front of the pump it will be "vacuum" */
                var changedType = null;
                if (currentNode.type === NodeComponentType.CENTRALIZED_VACUUM_PUMP) {
                    changedType = attachment.tubeType;
                }
                var currentEdge = state.edges[attachment.edgeId];
                currentEdge.tubeType = changedType || currentType;
                var otherAttachment = currentEdge.fromAttachmentId === attachmentId
                    ? state.attachments[currentEdge.toAttachmentId]
                    : state.attachments[currentEdge.fromAttachmentId];
                var otherNode = state.nodes[otherAttachment.nodeId];
                traversed.add(attachment.edgeId);
                recursiveUpdateTubes(otherNode, changedType || currentType);
            }
        });
    };
    /* Start the traversal from the feed pressure point and with type "compressed_air"*/
    recursiveUpdateTubes(state.nodes["feed-pressure-point"], TubeType.COMPRESSED_AIR);
    try {
        /* The network may contain node and edges that are not yet connected to the feed
         * pressure point. These should all have type "unknown" */
        /* Go through all edges, if they have not been traversed in the recursive function
         * they should have type "unknown".
         * However the tube connections connected to the edges might have a leaf node with a set tubeType.
         * To figure out if that is the case, we recursively travel across all edges to find a disconnected cup/pump
         * which will determine the tubeType of the disconnected tube connection and all its attachments */
        for (var _b = __values(Object.values(state.edges)), _c = _b.next(); !_c.done; _c = _b.next()) {
            var edge = _c.value;
            if (traversed.has(edge.id))
                continue;
            notTraversed.add(edge.id);
            var fromAttachment = state.attachments[edge.fromAttachmentId];
            var fromNode = state.nodes[fromAttachment.nodeId];
            var toAttachment = state.attachments[edge.toAttachmentId];
            var toNode = state.nodes[toAttachment.nodeId];
            var tubeType = recursivelyLookForTubeType(state, fromAttachment, new Set());
            // set the tube type for this edge and for the attachments it connects to
            edge.tubeType = tubeType;
            state.attachments[edge.fromAttachmentId].tubeType = tubeType;
            state.attachments[edge.toAttachmentId].tubeType = tubeType;
            // if the connected nodes are TubeConnectors, set the type on these as well
            if (fromNode.component.tubeType) {
                setTubeConnectionType(state, fromNode, tubeType);
            }
            if (toNode.component.tubeType) {
                setTubeConnectionType(state, toNode, tubeType);
            }
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_2) throw e_2.error; }
    }
    resetCompletetlyDisconnectedTubeConnections(state);
    state.hasDisconnectedEdges = notTraversed.size > 0;
    return state;
};
// look at all connected nodes and see if we can find a node that is not a Tube Connection and that has a known type
var recursivelyLookForTubeType = function (state, attachment, traversedEdges) {
    var e_3, _a;
    var node = state.nodes[attachment.nodeId];
    var attachmentsToInvestigate = node.type === NodeComponentType.TUBE_CONNECTION
        ? node.attachmentIds
        : [attachment.id];
    try {
        for (var attachmentsToInvestigate_1 = __values(attachmentsToInvestigate), attachmentsToInvestigate_1_1 = attachmentsToInvestigate_1.next(); !attachmentsToInvestigate_1_1.done; attachmentsToInvestigate_1_1 = attachmentsToInvestigate_1.next()) {
            var attachmentId = attachmentsToInvestigate_1_1.value;
            var attachment_1 = state.attachments[attachmentId];
            if (attachment_1.tubeType !== TubeType.UNKNOWN &&
                node.type !== NodeComponentType.TUBE_CONNECTION) {
                // found an attachment with known tube type
                return attachment_1.tubeType;
            }
            if (!attachment_1.edgeId || traversedEdges.has(attachment_1.edgeId))
                continue;
            traversedEdges.add(attachment_1.edgeId);
            var edge = state.edges[attachment_1.edgeId];
            var nextAttachment = edge.fromAttachmentId === attachment_1.id
                ? state.attachments[edge.toAttachmentId]
                : state.attachments[edge.fromAttachmentId];
            var tubeType = recursivelyLookForTubeType(state, nextAttachment, traversedEdges);
            if (tubeType !== TubeType.UNKNOWN)
                return tubeType;
        }
    }
    catch (e_3_1) { e_3 = { error: e_3_1 }; }
    finally {
        try {
            if (attachmentsToInvestigate_1_1 && !attachmentsToInvestigate_1_1.done && (_a = attachmentsToInvestigate_1.return)) _a.call(attachmentsToInvestigate_1);
        }
        finally { if (e_3) throw e_3.error; }
    }
    return TubeType.UNKNOWN;
};
/* If a node is completely disconnected and has no edges it should have type "unknown" */
var resetCompletetlyDisconnectedTubeConnections = function (state) {
    var e_4, _a;
    try {
        for (var _b = __values(Object.values(state.nodes)), _c = _b.next(); !_c.done; _c = _b.next()) {
            var node = _c.value;
            if (node.type !== NodeComponentType.TUBE_CONNECTION)
                continue;
            var hasEdgeConnections = node.attachmentIds.some(function (attachmentId) { return state.attachments[attachmentId].edgeId; });
            if (hasEdgeConnections)
                continue;
            setTubeConnectionType(state, node, TubeType.UNKNOWN);
        }
    }
    catch (e_4_1) { e_4 = { error: e_4_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_4) throw e_4.error; }
    }
};
var setTubeConnectionType = function (state, node, type) {
    var tubeConnection = node.component;
    tubeConnection.tubeType = type;
    node.attachmentIds.forEach(function (attachmentId) {
        var attachment = state.attachments[attachmentId];
        attachment.tubeType = type;
    });
};
export var defaultNodeWidth = 0.1;
export var findFreeNodePosition = function (newPosition, existingNodes, type) {
    var e_5, _a, e_6, _b;
    var occupiedPositions = [];
    try {
        for (var _c = __values(Object.values(existingNodes)), _d = _c.next(); !_d.done; _d = _c.next()) {
            var node = _d.value;
            // We ignore nodes of different types as they don't occlude the visuals when overlapping.
            if (node.type !== type) {
                continue;
            }
            var width = node.type === NodeComponentType.GRIP_POSITION
                ? node.component.width
                : defaultNodeWidth;
            occupiedPositions.push({
                position: node.position,
                width: width,
            });
        }
    }
    catch (e_5_1) { e_5 = { error: e_5_1 }; }
    finally {
        try {
            if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
        }
        finally { if (e_5) throw e_5.error; }
    }
    var isPositionOccupied = function (newNode, existingNodes) {
        var e_7, _a;
        try {
            for (var existingNodes_1 = __values(existingNodes), existingNodes_1_1 = existingNodes_1.next(); !existingNodes_1_1.done; existingNodes_1_1 = existingNodes_1.next()) {
                var existing = existingNodes_1_1.value;
                var dx = Math.abs(newNode.position.x - existing.position.x);
                var dz = Math.abs(newNode.position.z - existing.position.z);
                var overlapX = dx < (newNode.width + existing.width) / 2;
                var overlapZ = dz < (newNode.width + existing.width) / 2;
                if (overlapX && overlapZ)
                    return true;
            }
        }
        catch (e_7_1) { e_7 = { error: e_7_1 }; }
        finally {
            try {
                if (existingNodes_1_1 && !existingNodes_1_1.done && (_a = existingNodes_1.return)) _a.call(existingNodes_1);
            }
            finally { if (e_7) throw e_7.error; }
        }
        return false;
    };
    if (!isPositionOccupied(newPosition, occupiedPositions)) {
        return newPosition.position;
    }
    var directions = [
        { x: 1, z: 0 },
        { x: -1, z: 0 },
        { x: 0, z: 1 },
        { x: 0, z: -1 },
        { x: 1, z: 1 },
        { x: -1, z: 1 },
        { x: 1, z: -1 },
        { x: -1, z: -1 },
    ];
    var attempts = 0;
    var queue = [newPosition.position];
    var visited = new Set();
    var key = function (v) { return "".concat(v.x, ",").concat(v.z); };
    var maxTries = 60;
    while (queue.length > 0 && attempts < maxTries) {
        var current = queue.shift();
        try {
            for (var directions_1 = (e_6 = void 0, __values(directions)), directions_1_1 = directions_1.next(); !directions_1_1.done; directions_1_1 = directions_1.next()) {
                var dir = directions_1_1.value;
                var candidate = {
                    x: current.x + dir.x * newPosition.width,
                    y: newPosition.position.y, // Keep same Y
                    z: current.z + dir.z * newPosition.width,
                };
                if (visited.has(key(candidate)))
                    continue;
                visited.add(key(candidate));
                var testNode = { position: candidate, width: newPosition.width };
                if (!isPositionOccupied(testNode, occupiedPositions)) {
                    return candidate;
                }
                queue.push(candidate);
                attempts++;
            }
        }
        catch (e_6_1) { e_6 = { error: e_6_1 }; }
        finally {
            try {
                if (directions_1_1 && !directions_1_1.done && (_b = directions_1.return)) _b.call(directions_1);
            }
            finally { if (e_6) throw e_6.error; }
        }
    }
    return null; // No free position found
};
